// src/components/Register.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Fade,
  InputAdornment,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { PersonAdd as PersonAddIcon, Visibility, VisibilityOff } from '@mui/icons-material';

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Password strength checker
const checkPasswordStrength = (password) => {
  let score = 0;
  let feedback = [];
  
  if (password.length >= 8) score += 1;
  else feedback.push('At least 8 characters');
  
  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('One uppercase letter');
  
  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('One lowercase letter');
  
  if (/\d/.test(password)) score += 1;
  else feedback.push('One number');
  
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
  else feedback.push('One special character');
  
  const strength = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'][score];
  const color = ['error', 'error', 'warning', 'info', 'success'][score];
  
  return { score, strength, color, feedback, percentage: (score / 5) * 100 };
};

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    initialBalance: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ 
    score: 0, 
    strength: 'Very Weak', 
    color: 'error', 
    feedback: [], 
    percentage: 0 
  });
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Update password strength when password changes
    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
    
    if (error) setError('');
    if (rateLimitInfo) setRateLimitInfo(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Check network status first
    if (!navigator.onLine) {
      setError('No internet connection. Please check your network and try again.');
      setLoading(false);
      return;
    }

    // Enhanced validation
    if (!formData.email || !isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    if (!formData.password || passwordStrength.score < 3) {
      setError('Password must meet security requirements: at least 8 characters, with uppercase, lowercase, number, and special character');
      setLoading(false);
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    if (!formData.initialBalance || Number(formData.initialBalance) <= 0) {
      setError('Initial balance must be greater than zero');
      setLoading(false);
      return;
    }
    
    if (Number(formData.initialBalance) > 1000000) {
      setError('Initial balance cannot exceed $1,000,000');
      setLoading(false);
      return;
    }

    try {
      console.log('Sending registration request:', { 
        email: formData.email.trim().toLowerCase(),
        initialBalance: Number(formData.initialBalance)
      });
      
      const response = await api.post('/api/auth/register', {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        initialBalance: Number(formData.initialBalance),
      });
      
      const { accessToken, refreshToken, user } = response.data;
      
      if (!accessToken) {
        throw new Error('No access token received from registration');
      }
      
      console.log('Registration successful, received tokens and user:', { 
        accessToken: !!accessToken, 
        refreshToken: !!refreshToken,
        user: user 
      });
      
      // Store tokens with consistent naming
      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      // Store user data if provided in response
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
        console.log('User data stored from registration response:', user);
      }

      // Try to fetch user data if not provided in response
      if (!user) {
        try {
          console.log('Fetching user data after registration');
          const userResponse = await api.get('/api/auth/user');
          console.log('User response after registration:', userResponse.data);
          
          if (userResponse.data) {
            localStorage.setItem('user', JSON.stringify(userResponse.data));
            console.log('User data stored from user endpoint:', userResponse.data);
          }
        } catch (userErr) {
          console.warn('Failed to fetch user after registration, proceeding with tokens only:', userErr);
          // Don't throw error here - we can still navigate
        }
      }

      // DETERMINE NAVIGATION PATH - FIXED LOGIC
      let redirectPath = '/dashboard'; // DEFAULT PATH FOR ALL USERS
      
      // Check user role from available sources
      const userData = user || JSON.parse(localStorage.getItem('user') || '{}');
      console.log('User data for navigation decision:', userData);
      
      // Only redirect to superadmin if explicitly superadmin
      if (userData.role === 'superadmin') {
        redirectPath = '/superadmin';
        console.log('Redirecting to superadmin dashboard');
      } else {
        console.log('Redirecting to regular user dashboard');
      }
      
      console.log('Final navigation path:', redirectPath);
      
      // Navigate to the determined path
      navigate(redirectPath, { replace: true });
      
    } catch (err) {
      console.error('Registration error:', err);
      let errorMsg = 'Registration failed. Please try again.';
      
      if (err.message === 'Network is offline. Please check your connection.') {
        errorMsg = 'No internet connection. Please check your network and try again.';
      } else if (err.response?.status === 429) {
        errorMsg = 'Too many registration attempts. Please wait 15 minutes before trying again.';
        setRateLimitInfo({
          resetTime: Date.now() + (15 * 60 * 1000),
          message: 'Rate limit will reset in 15 minutes'
        });
      } else if (err.response?.status === 400) {
        if (err.response.data?.errors && Array.isArray(err.response.data.errors)) {
          errorMsg = `Registration failed: ${err.response.data.errors.join(', ')}`;
        } else {
          errorMsg = err.response.data?.msg || 'Registration failed due to validation errors.';
        }
      } else if (err.response?.status === 409) {
        errorMsg = 'An account with this email already exists. Please use a different email or try logging in.';
      } else if (err.response?.status === 500) {
        errorMsg = 'Server error. Please try again later or contact support if the problem persists.';
      } else if (err.response?.data?.msg) {
        errorMsg = err.response.data.msg;
      } else if (err.message) {
        errorMsg = err.message;
      } else if (err.code === 'NETWORK_ERROR') {
        errorMsg = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMsg);
      
      // Clear tokens on error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(prev => !prev);
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box 
        sx={{ 
          marginTop: 8, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}
      >
        <Paper 
          elevation={6} 
          sx={{ 
            padding: 4, 
            width: '100%', 
            position: 'relative', 
            borderRadius: 2,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <PersonAddIcon 
              sx={{ 
                fontSize: 40, 
                color: 'primary.main', 
                mb: 2,
                background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)',
                borderRadius: '50%',
                p: 1
              }} 
            />
            <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Traders Journal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Create your account
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2, 
                wordBreak: 'break-word',
                borderRadius: 1
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {rateLimitInfo && (
            <Alert 
              severity="warning" 
              sx={{ 
                mt: 2,
                borderRadius: 1
              }}
            >
              <Typography variant="body2">
                {rateLimitInfo.message}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                You can try again after: {new Date(rateLimitInfo.resetTime).toLocaleTimeString()}
              </Typography>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              type="email"
              error={!!error && error.includes('email')}
              helperText={error && error.includes('email') ? error : ''}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 1 
                } 
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              error={!!error && error.includes('Password')}
              helperText={error && error.includes('Password') ? error : 'Must meet security requirements'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      disabled={loading}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 1 
                } 
              }}
            />
            
            {/* Password Strength Indicator */}
            {formData.password && (
              <Box sx={{ mt: 1, mb: 1 }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 0.5 
                }}>
                  <Typography variant="caption" color="text.secondary">
                    Password Strength:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color={`${passwordStrength.color}.main`} 
                    sx={{ fontWeight: 'bold' }}
                  >
                    {passwordStrength.strength}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength.percentage}
                  color={passwordStrength.color}
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: 'grey.200'
                  }}
                />
                {passwordStrength.feedback.length > 0 && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 0.5, 
                      display: 'block',
                      fontStyle: 'italic'
                    }}
                  >
                    Requirements: {passwordStrength.feedback.join(', ')}
                  </Typography>
                )}
              </Box>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              disabled={loading}
              error={!!error && error.includes('Passwords do not match')}
              helperText={error && error.includes('Passwords do not match') ? error : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleConfirmPasswordVisibility}
                      edge="end"
                      disabled={loading}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 1 
                } 
              }}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="initialBalance"
              label="Initial Balance (USD)"
              type="number"
              id="initialBalance"
              value={formData.initialBalance}
              onChange={handleChange}
              disabled={loading}
              inputProps={{ 
                min: "0.01", 
                step: "0.01", 
                max: "1000000",
                pattern: "[0-9]*[.,]?[0-9]*"
              }}
              error={!!error && error.includes('balance')}
              helperText={error && error.includes('balance') ? error : 'Enter amount between $0.01 and $1,000,000'}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 1 
                } 
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5, 
                borderRadius: 1, 
                textTransform: 'none', 
                fontSize: '1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #1976d2 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1565c0 30%, #00acc1 90%)',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .5)',
                },
                '&:disabled': {
                  background: 'grey.400',
                  boxShadow: 'none',
                }
              }}
              disabled={loading || passwordStrength.score < 3}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={24} sx={{ color: 'white', mr: 1 }} />
                  Creating Account...
                </Box>
              ) : (
                'Sign Up'
              )}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Typography 
                  variant="body2" 
                  color="primary" 
                  sx={{ 
                    '&:hover': { 
                      textDecoration: 'underline',
                      color: 'primary.dark'
                    } 
                  }}
                >
                  Already have an account? Sign In
                </Typography>
              </Link>
            </Box>
          </Box>

          {/* Loading Overlay */}
          {loading && (
            <Fade in={loading} timeout={500}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  backdropFilter: 'blur(4px)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress
                  size={50}
                  thickness={4}
                  sx={{
                    color: 'primary.main',
                    animation: 'spin 1.5s linear infinite, glow 1.5s ease-in-out infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                    '@keyframes glow': {
                      '0%': { 
                        filter: 'drop-shadow(0 0 2px #1976d2)',
                        transform: 'scale(1)'
                      },
                      '50%': { 
                        filter: 'drop-shadow(0 0 10px #21CBF3)',
                        transform: 'scale(1.1)'
                      },
                      '100%': { 
                        filter: 'drop-shadow(0 0 2px #1976d2)',
                        transform: 'scale(1)'
                      },
                    },
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    mt: 3,
                    color: 'white',
                    fontWeight: 'bold',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 0.8 },
                      '50%': { opacity: 1 },
                      '100%': { opacity: 0.8 },
                    },
                  }}
                >
                  Creating Your Account...
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: 'grey.300',
                    textAlign: 'center',
                    maxWidth: '80%'
                  }}
                >
                  Setting up your trading journal and initial balance
                </Typography>
              </Box>
            </Fade>
          )}
        </Paper>

        <Typography 
          variant="body2" 
          color="text.secondary" 
          align="center" 
          sx={{ 
            mt: 4,
            fontStyle: 'italic',
            maxWidth: '80%'
          }}
        >
          "Invest your money in foreign trade, and one of these days you will make a profit." 
          <br />
          Ecclesiastes 11:1
        </Typography>
      </Box>
    </Container>
  );
};

export default Register;
